import Vue from "vue";
import Router from "vue-router";
import store from "@/stores/store";
import i18n from "@/plugins/i18n.js";

// Import components
// const Home = () => import("@/views/Home.vue");
const Home = () => import("@/views/Landing.vue");
const CommonParent = () => import("@/views/CommonParent.vue");
const CleanDashboard = () => import("@/views/CleanDashboard.vue");
const AdminDashboard = () => import("@/views/AdminDashboard.vue");

const Company = () => import("@/views/company/Company.vue");
const CompanyUsers = () => import("@/components/company/Users.vue");
const CompanyEdit = () => import("@/components/company/Edit.vue");
const CompanyCreate = () => import("@/components/company/Create.vue");
const CreateUser = () => import("@/components/company/CreateUser.vue");
const CompanyApps = () => import("@/components/company/Applications.vue");

const UserCompanyRegister = () => import("@/components/user/Register");
const EditUser = () => import("@/components/user/Detail");

const ForgotPassword = () => import("@/views/ForgotPassword.vue");
const ForgotPasswordComponent = () =>
  import("@/components/password/ForgotPasswordComponent.vue");
const NewPassword = () => import("@/components/password/NewPassword.vue");

const Application = () => import("@/views/Application.vue");
const ApplicationCreate = () => import("@/components/application/Create.vue");
const ApplicationEdit = () => import("@/components/application/Edit.vue");

const Tag = () => import("@/views/Tag.vue");
const TagList = () => import("@/components/tag/Tag.vue");
const TagCreate = () => import("@/components/tag/Create.vue");
const TagPool = () => import("@/components/tag/TagPool.vue");

const TagDetail = () => import("@/views/TagDetail.vue");

const Alarm = () => import("@/views/Alarm.vue");
const AlarmCreate = () => import("@/components/alarm/Create.vue");
const AlarmEdit = () => import("@/components/alarm/Edit.vue");
const AlarmList = () => import("@/components/alarm/AlarmList.vue");
const ActiveAlarms = () => import("@/components/alarm/ActiveAlarms.vue");

const Login = () => import("@/views/Login.vue");
const Register = () => import("@/views/Register.vue");
const Logout = () => import("@/views/Logout.vue");

const ScriptView = () => import("@/views/Script.vue");
const ScriptList = () => import("@/components/script/ScriptList.vue");
const ScriptCreate = () => import("@/components/script/ScriptCreate.vue");
const ScriptEdit = () => import("@/components/script/ScriptEdit.vue");

const Report = () => import("@/views/Report.vue");
const ReportList = () => import("@/components/report/ReportList.vue");
const ReportCreate = () => import("@/components/report/ReportCreate.vue");
const ReportEdit = () => import("@/components/report/ReportEdit.vue");
const DashboardModule = () => import("@/components/common/ModuleGrid");
const DashboardPublic = () => import("@/views/DashboardPublic.vue");

const Contact = () => import("@/views/Contact.vue");
const News = () => import("@/views/News.vue");
const SettingView = () => import("@/views/Setting.vue");
const Settings = () => import("@/components/setting/SettingEdit.vue");

const SingalTest = () => import("@/views/SignalTest.vue");
const PeekSignalTest = () =>
  import("@/components/signaltest/PeekSignalTest.vue");

const GatewayView = () => import("@/views/Gateway.vue");
const GatewayList = () => import("@/components/gateway/GatewayList.vue");
const GatewayCreate = () => import("@/components/gateway/CreateGateway.vue");

import { Roles } from "@/_helpers/Role";

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: "/",
      name: "home",
      component: Home,
      meta: { authorize: [] },
    },

    {
      path: "/settings",
      component: SettingView,
      meta: { authorize: Roles.CompanyOrUser },
      children: [
        {
          path: "",
          name: "setting/edit",
          component: Settings,
        },
      ],
    },

    {
      path: "/dashboard",
      name: "dashboardChooser",
      component: CleanDashboard,
      meta: { authorize: Roles.CompanyOrUser },
    },

    {
      path: "/dashboard/:id",
      component: DashboardModule,
      meta: { authorize: Roles.CompanyOrUser },
    },

    {
      path: "/public/:id",
      component: DashboardPublic,
      meta: { authorize: [] },
    },

    {
      path: "/alarm",
      component: Alarm,
      children: [
        {
          path: "",
          name: "alarm",
          component: AlarmList,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "active",
          component: ActiveAlarms,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "create",
          component: AlarmCreate,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
        {
          path: ":id",
          component: AlarmEdit,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
      ],
    },

    {
      path: "/gateways",
      component: GatewayView,
      children: [
        {
          path: "",
          component: GatewayList,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "create",
          component: GatewayCreate,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
      ],
    },

    {
      path: "/signaltest",
      component: CommonParent,
      meta: { authorize: Roles.CompanyOrUser },
      children: [
        {
          path: "",
          component: SingalTest,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "peek/:id",
          component: PeekSignalTest,
          meta: { authorize: Roles.CompanyOrUser },
        },
      ],
    },

    {
      path: "/script",
      component: ScriptView,
      children: [
        {
          path: "",
          component: ScriptList,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },

        {
          path: "create",
          component: ScriptCreate,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },

        {
          path: ":id",
          component: ScriptEdit,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
      ],
    },

    {
      path: "/company",
      component: Company,
      children: [
        {
          path: "",
          component: CompanyUsers,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "edit",
          component: CompanyEdit,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
        {
          path: "create",
          component: CompanyCreate,
          meta: { authorize: Roles.Admin },
        },
        {
          path: "applications",
          component: CompanyApps,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "usercreate",
          component: CreateUser,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
        {
          path: "user/:id",
          component: EditUser,
          meta: { authorize: Roles.CompanyOrUser },
        },
      ],
    },

    {
      path: "/forgot",
      component: ForgotPassword,
      children: [
        {
          path: "",
          component: ForgotPasswordComponent,
        },
        {
          path: ":id",
          component: NewPassword,
        },
      ],
    },

    {
      path: "/application",
      component: Application,
      children: [
        {
          path: "",
          component: CompanyApps,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "create",
          component: ApplicationCreate,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
        {
          path: ":id",
          component: ApplicationEdit,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
      ],
    },

    {
      path: "/superadmin",
      component: AdminDashboard,
      meta: { authorize: Roles.SuperAdmin },
    },

    {
      path: "/report",
      component: Report,
      children: [
        {
          path: "",
          component: ReportList,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
        {
          path: "create",
          component: ReportCreate,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },

        {
          path: ":id",
          component: ReportEdit,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
      ],
    },

    {
      path: "/tag",
      component: Tag,
      children: [
        {
          path: "",
          component: TagList,
          meta: { authorize: Roles.CompanyOrUser },
        },
        {
          path: "create",
          component: TagCreate,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
        {
          path: "create/:deveui",
          component: TagCreate,
          meta: { authorize: Roles.AdminOrCompanyAdmin },
        },
        {
          path: "pool",
          component: TagPool,
          meta: { authorize: Roles.SuperAdmin },
        },
      ],
    },

    {
      path: "/tagdetails/:deveui",
      component: TagDetail,
      meta: { authorize: Roles.AdminOrCompanyAdmin },
    },

    {
      path: "/register",
      component: Register,
      meta: { authorize: Roles.SuperAdmin },
      children: [
        {
          path: "",
          component: UserCompanyRegister,
          meta: { authorize: Roles.SuperAdmin },
        },
      ],
    },

    {
      path: "/login",
      name: "Login",
      component: Login,
    },
    {
      path: "/logout",
      name: "Logout",
      component: Logout,
    },

    {
      path: "/contact",
      name: "Contact",
      component: Contact,
    },

    {
      path: "/news",
      name: "News",
      component: News,
    },
  ],

  duplicateNavigationPolicy: "reload",
});

router.beforeEach((to, from, next) => {
  if (
    store.state.languages !== undefined &&
    store.state.languages.language &&
    store.state.languages.language !== i18n.locale
  ) {
    i18n.locale = store.state.languages.language;
    next();
  } else if (
    store.state.languages === undefined ||
    !store.state.languages.language
  ) {
    store
      .dispatch("languages/setLanguage", navigator.languages, { root: true })
      .then(() => {
        i18n.locale = store.state.languages.language;
        next();
      });
  }

  if (to.meta.authorize && to.meta.authorize.length > 0) {
    var role = store.state.users.currentUser.role;

    if (!role) next({ path: "/login" });

    if (to.meta.authorize.includes(role)) {
      next();
    } else {
      next({ path: "/" });
    }
  }

  next();
});

export default router;
